import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Box,
  Typography,
  Button,
  InputAdornment,
  alpha,
  LinearProgress,
  useTheme,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  Info as InfoIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import StatusDialog from './StatusDialog';
import userService from '../../../services/userService';

// Password strength checker
const calculatePasswordStrength = (password) => {
  if (!password) return 0;
  
  let strength = 0;
  
  // Length check
  if (password.length >= 8) strength += 25;
  
  // Lowercase check
  if (password.match(/[a-z]/)) strength += 25;
  
  // Uppercase check
  if (password.match(/[A-Z]/)) strength += 25;
  
  // Number or special character check
  if (password.match(/[0-9]/) || password.match(/[^a-zA-Z0-9]/)) strength += 25;
  
  return strength;
};

const getPasswordStrengthColor = (strength) => {
  if (strength < 25) return '#f44336'; // Weak - Red
  if (strength < 50) return '#ff9800'; // Fair - Orange
  if (strength < 75) return '#ffeb3b'; // Good - Yellow
  return '#4caf50'; // Strong - Green
};

const getPasswordStrengthLabel = (strength) => {
  if (strength < 25) return 'Weak';
  if (strength < 50) return 'Fair';
  if (strength < 75) return 'Good';
  return 'Strong';
};

const PasswordCriteria = ({ password }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const criteria = [
    { 
      label: 'At least 8 characters long', 
      met: password && password.length >= 8 
    },
    { 
      label: 'Contains a lowercase letter', 
      met: password && password.match(/[a-z]/) 
    },
    { 
      label: 'Contains an uppercase letter', 
      met: password && password.match(/[A-Z]/) 
    },
    { 
      label: 'Contains a number or special character', 
      met: password && (password.match(/[0-9]/) || password.match(/[^a-zA-Z0-9]/)) 
    }
  ];
  
  return (
    <Box sx={{ mt: 2, mb: 3 }}>
      <Typography 
        variant="subtitle2" 
        sx={{ 
          mb: 1,
          color: isDarkMode ? '#b3b3b3' : 'text.secondary'
        }}
        gutterBottom
      >
        Password must:
      </Typography>
      {criteria.map((criterion, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          {criterion.met ? (
            <CheckIcon 
              fontSize="small" 
              sx={{ 
                color: theme.palette.success.main,
                mr: 1 
              }} 
            />
          ) : (
            <InfoIcon 
              fontSize="small" 
              sx={{ 
                color: isDarkMode 
                  ? alpha('#b3b3b3', 0.5)
                  : alpha(theme.palette.text.secondary, 0.5),
                mr: 1 
              }} 
            />
          )}
          <Typography 
            variant="body2" 
            sx={{
              color: criterion.met 
                ? (isDarkMode ? '#ffffff' : 'text.primary')
                : (isDarkMode ? '#b3b3b3' : 'text.secondary')
            }}
          >
            {criterion.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const ChangePassword = ({ open, onClose }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [passwordError, setPasswordError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Status Dialog states
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusDialogProps, setStatusDialogProps] = useState({
    title: 'Success',
    message: '',
    type: 'success',
    buttonText: 'Continue'
  });
  
  const passwordStrength = calculatePasswordStrength(newPassword);
  
  const handleTogglePasswordVisibility = (field) => {
    switch (field) {
      case 'current':
        setShowCurrentPassword(!showCurrentPassword);
        break;
      case 'new':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirm':
        setShowConfirmPassword(!showConfirmPassword);
        break;
      default:
        break;
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (passwordStrength < 50) {
      setPasswordError('New password is not strong enough');
      return;
    }
    
    // Clear errors if validation passes
    setPasswordError('');
    setIsLoading(true);
    
    try {
      // Make API call to change password
      await userService.changePassword(currentPassword, newPassword);
      
      // Show success dialog
      setStatusDialogProps({
        title: 'Password Changed',
        message: 'Your password has been updated successfully. A confirmation has been sent to your email address.',
        type: 'success',
        buttonText: 'Continue'
      });
      setStatusDialogOpen(true);
      
      // Reset form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
    } catch (error) {
      console.error('Error changing password:', error);
      
      setStatusDialogProps({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to change password. Please try again.',
        type: 'error',
        buttonText: 'Try Again'
      });
      setStatusDialogOpen(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false);
    if (statusDialogProps.type === 'success') {
      onClose(); // Close the password dialog only on success
    }
  };
  
  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: isDarkMode 
              ? '0 8px 40px rgba(0, 0, 0, 0.5)'
              : '0 8px 40px rgba(0, 0, 0, 0.12)',
            backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
          }
        }}
      >
        <DialogTitle sx={{ 
          m: 0, 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
          color: isDarkMode ? '#ffffff' : 'inherit'
        }}>
          <Typography variant="h6" component="div" sx={{
            fontWeight: 600,
            color: isDarkMode ? '#ffffff' : 'inherit'
          }}>
            Change Password
          </Typography>
          <IconButton
            edge="end"
            onClick={onClose}
            aria-label="close"
            disabled={isLoading}
            sx={{ color: isDarkMode ? '#ffffff' : 'inherit' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers sx={{ 
          p: 3,
          backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
          borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.12)'
        }}>
          <form onSubmit={handleSubmit}>
            {passwordError && (
              <Box sx={{ 
                backgroundColor: isDarkMode 
                  ? alpha(theme.palette.error.main, 0.2)
                  : alpha(theme.palette.error.main, 0.1),
                color: isDarkMode ? '#ffffff' : theme.palette.error.main,
                p: 2,
                borderRadius: 1,
                mb: 3,
                border: isDarkMode ? '1px solid rgba(244, 67, 54, 0.3)' : undefined
              }}>
                <Typography variant="body2">{passwordError}</Typography>
              </Box>
            )}
            
            <TextField
              label="Current Password"
              variant="outlined"
              fullWidth
              margin="normal"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              InputProps={{
                sx: {
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  }
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handleTogglePasswordVisibility('current')}
                      edge="end"
                      disabled={isLoading}
                      sx={{ color: isDarkMode ? '#b3b3b3' : 'inherit' }}
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                sx: { color: isDarkMode ? '#b3b3b3' : 'inherit' }
              }}
              disabled={isLoading}
            />
            
            <TextField
              label="New Password"
              variant="outlined"
              fullWidth
              margin="normal"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              InputProps={{
                sx: {
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  }
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handleTogglePasswordVisibility('new')}
                      edge="end"
                      disabled={isLoading}
                      sx={{ color: isDarkMode ? '#b3b3b3' : 'inherit' }}
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                sx: { color: isDarkMode ? '#b3b3b3' : 'inherit' }
              }}
              disabled={isLoading}
            />
            
            {newPassword && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ color: isDarkMode ? '#b3b3b3' : 'text.secondary' }}
                  >
                    Password Strength
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: getPasswordStrengthColor(passwordStrength),
                      fontWeight: 600
                    }}
                  >
                    {getPasswordStrengthLabel(passwordStrength)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: isDarkMode 
                      ? alpha('#ffffff', 0.1)
                      : alpha(theme.palette.grey[500], 0.1),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      backgroundColor: getPasswordStrengthColor(passwordStrength),
                    },
                  }}
                />
              </Box>
            )}
            
            <PasswordCriteria password={newPassword} />
            
            <TextField
              label="Confirm New Password"
              variant="outlined"
              fullWidth
              margin="normal"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmPassword && newPassword !== confirmPassword}
              helperText={
                confirmPassword && newPassword !== confirmPassword 
                  ? "Passwords don't match" 
                  : ""
              }
              InputProps={{
                sx: {
                  backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : 'inherit',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: isDarkMode ? '#404040' : 'rgba(0, 0, 0, 0.23)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  }
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handleTogglePasswordVisibility('confirm')}
                      edge="end"
                      disabled={isLoading}
                      sx={{ color: isDarkMode ? '#b3b3b3' : 'inherit' }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{
                sx: { color: isDarkMode ? '#b3b3b3' : 'inherit' }
              }}
              FormHelperTextProps={{
                sx: { color: isDarkMode ? '#b3b3b3' : 'inherit' }
              }}
              disabled={isLoading}
            />
            
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                onClick={onClose} 
                disabled={isLoading}
                sx={{
                  color: isDarkMode ? '#ffffff' : '#667eea',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 1.5,
                  px: 3,
                  '&:hover': {
                    backgroundColor: isDarkMode 
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(102, 126, 234, 0.04)',
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, #4a5568 0%, #553c8b 100%)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#ffffff',
                  fontWeight: 600,
                  borderRadius: 1.5,
                  px: 3,
                  boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                  '&:hover': {
                    background: isDarkMode
                      ? 'linear-gradient(135deg, #3a4454 0%, #453177 100%)'
                      : 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {isLoading ? 'Changing...' : 'Change Password'}
              </Button>
            </Box>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Status Dialog for success/error */}
      <StatusDialog
        open={statusDialogOpen}
        onClose={handleCloseStatusDialog}
        title={statusDialogProps.title}
        message={statusDialogProps.message}
        buttonText={statusDialogProps.buttonText}
        type={statusDialogProps.type}
      />
    </>
  );
};

export default ChangePassword;